import { TDeviceType } from '@cian/ui-kit';

import { keysToBitmask } from './keysToBitmask';
import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  getTermsValue,
  offerTypeFromJsonQuery,
} from '../../../common/packages/JsonQuery';
import { IJsonQuery } from '../../../common/packages/api-models/common/json_query';
import {
  TAdfoxMapBannerConfig,
  TAdfoxMapBannerConfigItem,
  TAdfoxMapBannerZoomRange,
} from '../../types/adfoxMapBannerConfig';

interface IGetAdfoxBannerConfigArgs {
  zoom: number;
  jsonQuery: IJsonQuery;
  deviceType: TDeviceType;
  adfoxEnabled: boolean;
  adfoxMapBannerConfig: TAdfoxMapBannerConfig;
  parentRegionId: number | null;
}

export function getAdfoxBannerConfig({
  zoom,
  jsonQuery,
  deviceType,
  adfoxEnabled,
  adfoxMapBannerConfig,
  parentRegionId,
}: IGetAdfoxBannerConfigArgs): TAdfoxMapBannerConfigItem | null {
  if (!adfoxEnabled) {
    return null;
  }

  const isDesktop = deviceType === 'desktop';

  return (
    adfoxMapBannerConfig.find(
      item =>
        matchesConfigItemWithoutZoom({ item, jsonQuery, parentRegionId }) && matchesZoom({ item, isDesktop, zoom }),
    ) ?? null
  );
}

/**
 * Проверяет соответствие элемента конфига по regionIds/offerTypes/dealTypes.
 * Зум не учитывается — удобно для SSR, где зум карты недоступен.
 *
 * Если в `jsonQuery.terms.region` ничего не выбрано, в качестве fallback
 * используется `parentRegionId` из состояния — это позволяет корректно
 * матчить Adfox при поиске по метро/району без явно выбранного региона.
 */
export function matchesConfigItemWithoutZoom({
  item,
  jsonQuery,
  parentRegionId,
}: {
  item: TAdfoxMapBannerConfigItem;
  jsonQuery: IJsonQuery;
  parentRegionId: number | null;
}): boolean {
  if (item.regionIds.length > 0) {
    const regionsSet = new Set(item.regionIds);
    const currentRegions = getTermsValue('region')(jsonQuery) ?? (parentRegionId != null ? [parentRegionId] : null);

    if (!currentRegions?.some(id => regionsSet.has(id))) {
      return false;
    }
  }

  const offerTypeMask = keysToBitmask(FOfferType, item.offerTypes);
  if (offerTypeMask !== 0 && (offerTypeFromJsonQuery(jsonQuery) & offerTypeMask) === 0) {
    return false;
  }

  const dealTypeMask = keysToBitmask(FDealType, item.dealTypes);
  if (dealTypeMask !== 0 && (dealTypeFromJsonQuery(jsonQuery) & dealTypeMask) === 0) {
    return false;
  }

  return true;
}

function matchesZoom({
  item,
  isDesktop,
  zoom,
}: {
  item: TAdfoxMapBannerConfigItem;
  isDesktop: boolean;
  zoom: number;
}): boolean {
  const zoomRange = isDesktop ? item.zoom?.desktop : item.zoom?.mobile;

  if (!zoomRange) {
    return true;
  }

  return isZoomInRange(zoom, zoomRange);
}

function isZoomInRange(zoom: number, { min, max }: TAdfoxMapBannerZoomRange): boolean {
  return zoom >= min && zoom <= max;
}
